/*
 * Copyright (C) Compliance Audit Systems Limited - All Rights Reserved
 * Unauthorised copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Sudara Fernando <sudara@complianceauditsystems.com>, ${DATE}
 *
 */

import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable, Optional, SkipSelf} from '@angular/core';
import {
    AcabimAuthRole,
    AcabimSolution,
    ApiService,
    EnvironmentModel,
    PaginatedOrNonPaginatedResponse,
    PaginatedRequest,
    SolutionEnvironmentModel
} from '@cas-hub/acabim-common-client';
import {NGXLogger} from 'ngx-logger';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {OrganisationSeat} from '../../_models/organisation/organisation-seats.model';

@Injectable({
    providedIn: 'root'
})
export class OrganisationSeatsService extends ApiService {

    protected controller = 'api/OrganisationSeat';

    constructor(protected readonly client: HttpClient,
                protected readonly environment: EnvironmentModel,
                @SkipSelf() @Optional() protected solutionEnvironment?: SolutionEnvironmentModel,
                @Optional() protected readonly logger?: NGXLogger) {
        super(client, environment, solutionEnvironment, logger);
        this.serverHost = environment.authority;
    }

    public getSeatOrders(pagination?: PaginatedRequest, overrideOrganisationId?: string): Observable<PaginatedOrNonPaginatedResponse<OrganisationSeat>> {
        const params = overrideOrganisationId ? new HttpParams().set('overrideOrganisationId', overrideOrganisationId) : undefined;
        return this.simplePaginatedGet(pagination, undefined, params);
    }

    public addPurchaseOrder(numberOfSeats: number, attachedSolution?: AcabimSolution, attachedRole?: AcabimAuthRole, overrideOrganisationId?: string):
        Observable<OrganisationSeat> {
        const body: any = {
            numberOfSeats
        };

        if (attachedRole !== undefined) {
            body.attachedRole = attachedRole;
        }

        if (attachedSolution !== undefined) {
            body.attachedSolution = attachedSolution;
        }

        if (overrideOrganisationId) {
            body.overrideOrganisationId = overrideOrganisationId;
        }

        return this.client.post(this.url(''), body, {
            headers: this.defaultHeaders,
            responseType: 'json'
        }).pipe(map(item => item as OrganisationSeat));
    }

    public removePurchaseOrder(seatId: number, overrideOrganisationId?: string): Observable<any> {
        const options = {
            headers: this.defaultHeaders,
            body: {overrideOrganisationId}
        };

        return this.client.delete(this.url(`${seatId}`), options);
    }

    public updatePurchaseOrder(seatId: number, numberOfSeats: number, overrideOrganisationId?: string): Observable<OrganisationSeat> {
        return this.client.put(this.url(`${seatId}`), {numberOfSeats, overrideOrganisationId}, {
            headers: this.defaultHeaders,
            responseType: 'json'
        }).pipe(map(i => i as OrganisationSeat));
    }

    public confirmPurchaseOrder(orderId: number, organisationId: string): Observable<any> {
        return this.client.post(this.url('confirm'), {orderId, overrideOrganisationId: organisationId}, {
            headers: this.defaultHeaders,
            responseType: 'json'
        });
    }

    public changeExpireDate(seatId: number, overrideOrganisationId?: string, expireDate?: string): Observable<OrganisationSeat> {
        return this.client.put(this.url(`${seatId}/expire`), {overrideOrganisationId, expireDate}, {
            headers: this.defaultHeaders,
            responseType: 'json'
        }).pipe(map(i => i as OrganisationSeat));
    }
}
